/*eslint-disable*/

import React, { useState } from "react";
import FullScreenModal from "./FullScreenModal";
import EditableTextField from "../Input/EditableTextField";
import LocationSearch from "../Search/LocationSearch";
import { useNavigate } from "react-router-dom"; // Import useNavigate for navigation

interface CreateClientModalProps {
    isOpen: boolean;
    onClose: () => void;
    onCreate: (clientData: {
        client_name: string;
        adresse: string;
        city: string;
        zip_code: string;
    }) => Promise<any>;
}

const CreateClientModal: React.FC<CreateClientModalProps> = ({
    isOpen,
    onClose,
    onCreate,
}) => {
    const [formData, setFormData] = useState({
        client_name: "",
        adresse: "",
        city: "",
        zip_code: "",
        lat: "",
        long: "",
    });

    const [errors, setErrors] = useState({
        client_name: false,
        adresse: false,
        city: false,
        zip_code: false,
        lat: false,
        long: false,
    });

    const [errorMessage, setErrorMessage] = useState(""); // State to hold error message
    const [showError, setShowError] = useState(false); // State to control the error popup visibility

    const [successMessage, setSuccessMessage] = useState(""); // State to hold success message
    const [showSuccess, setShowSuccess] = useState(false); // State to control the success popup visibility

    const navigate = useNavigate(); // Use navigate for redirection

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleAddressSelect = (
        street: string,
        city: string,
        zipCode: string,
        lat: string,
        long: string
    ) => {
        setFormData((prev) => ({
            ...prev,
            adresse: street,
            city: city,
            zip_code: zipCode,
            lat: lat,
            long: long,
        }));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        // Basic validation
        const newErrors = {
            client_name: formData.client_name.trim() === "",
            adresse: formData.adresse.trim() === "",
            city: formData.city.trim() === "",
            zip_code:
                formData.zip_code.trim() === "" ||
                !/^\d{5}$/.test(formData.zip_code),
            lat: false,
            long: false,
        };

        setErrors(newErrors);

        // Stop form submission if any field is invalid
        if (Object.values(newErrors).some((error) => error)) return;

        // Make API call to create client
        onCreate(formData)
            .then((newClient) => {
                // Display success message
                setSuccessMessage("Prospect créé avec succès !");
                setShowSuccess(true);
                // Reset form data
                setFormData({
                    client_name: "",
                    adresse: "",
                    city: "",
                    zip_code: "",
                    lat: "",
                    long: "",
                });
                // Close the modal after 1 second and redirect
                // Hide success message after 1 second
                setTimeout(() => {
                    setShowSuccess(false); // Hide success message
                    // Stay on the page for 1 second before redirecting
                    setTimeout(() => {
                        navigate(`/lead/${newClient.client_id}`); // Redirect to the new client page
                        onClose(); // Close modal after redirection
                    }, 1000); // Wait for another 1 second before redirecting
                }, 1000); // Show success message for 1 second
            })
            .catch((error) => {
                console.error("Error creating client:", error);
                setErrorMessage(
                    "Une erreur est survenue lors de la création du prospect."
                );
                setShowError(true);

                // Hide error after 3 seconds
                setTimeout(() => {
                    setShowError(false);
                    setErrorMessage(""); // Clear error message
                }, 3000);
            });
    };

    return (
        <FullScreenModal isOpen={isOpen} onClose={onClose}>
            <div className="w-full h-full z-40">
                <h2 className="text-2xl font-bold mb-5 text-left text-lg font bold">
                    Nouveau prospect
                </h2>
                <form
                    onSubmit={handleSubmit}
                    className="flex flex-col w-full md:w-1/3 space-y-2"
                >
                    <EditableTextField
                        label="Nom d'établissement"
                        name="client_name"
                        value={formData.client_name}
                        isEditing={true}
                        error={errors.client_name}
                        helperText={
                            errors.client_name
                                ? "Le nom d'établissement est requis"
                                : ""
                        }
                        onChange={handleChange}
                    />

                    <LocationSearch onAddressSelect={handleAddressSelect} />

                    <EditableTextField
                        label="Adresse"
                        name="adresse"
                        value={formData.adresse}
                        isEditing={false}
                        error={errors.adresse}
                        helperText={
                            errors.adresse ? "L'adresse est requise" : ""
                        }
                        onChange={handleChange}
                    />

                    <EditableTextField
                        label="Ville"
                        name="city"
                        value={formData.city}
                        isEditing={false}
                        error={errors.city}
                        helperText={errors.city ? "La ville est requise" : ""}
                        onChange={handleChange}
                    />

                    <EditableTextField
                        label="Code postal"
                        name="zip_code"
                        value={formData.zip_code}
                        isEditing={false}
                        error={errors.zip_code}
                        helperText={
                            errors.zip_code
                                ? "Veuillez entrer un code postal valide à 5 chiffres"
                                : ""
                        }
                        onChange={handleChange}
                    />

                    <button
                        type="submit"
                        className="bg-gold text-black font-bold px-4 py-2 rounded-full mt-5"
                    >
                        Créer prospect
                    </button>
                </form>
            </div>

            {/* Display error message as a small red popup */}
            {showError && (
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 mt-4 bg-rose-500 text-white p-3 rounded-md shadow-lg z-10">
                    {errorMessage}
                </div>
            )}

            {showSuccess && (
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 mt-4 bg-gold text-black p-3 rounded-md shadow-lg z-10">
                    {successMessage}
                </div>
            )}
        </FullScreenModal>
    );
};

export default CreateClientModal;
